/**
 * SinceLastSeen — "what's changed in your workspace since you last
 * looked" panel. Renders at the top of the dashboard on load.
 *
 * Stateless on the server side (the API is just a parallel read at
 * since=<iso>). The client tracks its own last-seen timestamp in
 * localStorage:
 *
 *   key:   `cmcp:last-seen:<workspaceId>`
 *   value: ISO timestamp of the previous dashboard load
 *
 * On mount:
 *   - If the key is unset (first ever load): set it to NOW and skip
 *     the panel — no signal yet.
 *   - Otherwise: fetch /since?since=<stashed timestamp>, render any
 *     non-empty section. The user can dismiss → key becomes NOW and
 *     the panel hides for this session.
 *   - On dismiss OR ~5s after the panel renders, the key updates so
 *     subsequent loads are scoped to "what's new since this view."
 *
 * Sections (only shown when non-empty):
 *   - Tracked bills that moved
 *   - New attention items (decisions queued)
 *   - Urgent/high policy intelligence imported by the user's AI
 *   - New briefings (press hits / morning clips)
 *   - Recent activity (workspace_events of any kind)
 *   - Your feedback: one summary row linking to the Feedback page (the
 *     route returns none for platform operators, whose tickets are the
 *     product backlog)
 *
 * The card shows the first SINCE_LAST_SEEN_ROW_LIMIT rows across sections,
 * with "Show all" for the rest.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Bot, Clock, MessageSquare, Newspaper, Siren, Sparkles, User, Zap } from "lucide-react";

interface SinceRollupBill {
  bill_ref: string;
  title: string | null;
  short_title: string | null;
  latest_action_date: string | null;
  latest_action_text: string | null;
}

interface SinceRollupAttentionItem {
  id: string;
  type: string;
  subject_type: string | null;
  subject_id: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
}

interface SinceRollupBriefing {
  id: string;
  title: string | null;
  briefing_date: string | null;
  item_count: number | null;
  created_at: string;
}

interface SinceRollupPanelComposition {
  id: string;
  page_kind: string;
  instance_id: string;
  composed_at: string;
  composed_by_user_id: string | null;
  composed_via: string;
  title: string | null;
  summary: string | null;
}

interface SinceRollupEvent {
  id: number;
  event_type: string;
  actor_type: "user" | "ai" | "system" | "scheduled" | "external";
  summary: string;
  subject_type: string | null;
  subject_id: string | null;
  subject_label: string | null;
  created_at: string;
}

interface SinceRollupTicketUpdate {
  ticket_number: string;
  type: string;
  status: "resolved" | "in_progress" | "needs_info";
  subject: string;
  resolution_summary: string | null;
  updated_at: string;
  resolved_at: string | null;
}

interface SinceRollupPolicyIntelligence {
  id: string;
  title: string;
  summary: string;
  category: string | null;
  priority_tier: "urgent" | "high";
  priority_score: number;
  source_count: number;
  status: string;
  evidence_revision: number;
  reviewed_evidence_revision: number;
  reviewed_at: string | null;
  needs_review: boolean;
  new_source_count: number;
  updated_since_review: boolean;
  last_seen_at: string;
  evidence_updated_at: string;
  updated_at: string;
}

interface SinceRollup {
  since: string;
  now: string;
  events: SinceRollupEvent[];
  bills_moved: SinceRollupBill[];
  new_attention_items: SinceRollupAttentionItem[];
  new_briefings: SinceRollupBriefing[];
  new_panel_compositions: SinceRollupPanelComposition[];
  recent_ticket_updates: SinceRollupTicketUpdate[];
  new_policy_intelligence: SinceRollupPolicyIntelligence[];
}

export interface SinceLastSeenProps {
  workspaceId: string | null | undefined;
}

function lastSeenKey(workspaceId: string): string {
  return `cmcp:last-seen:${workspaceId}`;
}

function readLastSeen(workspaceId: string): string | null {
  try {
    return localStorage.getItem(lastSeenKey(workspaceId));
  } catch {
    return null;
  }
}

function writeLastSeen(workspaceId: string, iso: string): void {
  try {
    localStorage.setItem(lastSeenKey(workspaceId), iso);
  } catch {
    /* ignore */
  }
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(1, Math.floor((now - then) / 1000));
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86400 * 7) return `${Math.floor(sec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function elapsedHuman(sinceIso: string): string {
  const sec = Math.max(1, Math.floor((Date.now() - new Date(sinceIso).getTime()) / 1000));
  if (sec < 3600) return `${Math.floor(sec / 60)} minute${Math.floor(sec / 60) === 1 ? "" : "s"}`;
  if (sec < 86400) {
    const h = Math.floor(sec / 3600);
    return `${h} hour${h === 1 ? "" : "s"}`;
  }
  const d = Math.floor(sec / 86400);
  return `${d} day${d === 1 ? "" : "s"}`;
}

const ATTENTION_LABELS: Record<string, string> = {
  whip_math_champion_gap: "Champion not yet cosponsoring",
  extraction_review: "AI signal needs review",
  cooling_relationship: "Relationship cooling",
  conflict: "Source conflict",
};

// Map (page_kind, instance_id) → click-through href for the
// SinceLastSeen "AI composed this" links. Mirrors the canonical URL
// contract documented in the system prompt (EntityKind table).
function panelHref(pageKind: string, instanceId: string): string {
  const inst = encodeURIComponent(instanceId);
  switch (pageKind) {
    case "ai_page":
      return `/app/reports/${inst}`;
    case "dashboard":
      return "/app/dashboard";
    case "bills":
      return "/app/bills";
    case "members":
      return "/app/members";
    case "bill_detail":
      return `/app/bills/${inst}`;
    case "member_detail":
      return `/app/members/${inst}`;
    case "whip_count_bill":
      return `/app/bills/${inst}/whip-count`;
    case "whip_count_issue":
      return `/app/issues/${inst}/whip-count`;
    case "issue_detail":
      return `/app/issues/${inst}`;
    case "committee_detail":
      return `/app/committees/${inst}`;
    case "presentation":
      return `/app/presentations/${inst}`;
    case "scorecards":
      return "/app/scorecards";
    case "reports":
      return "/app/reports";
    case "treasury":
      return "/app/treasury";
    case "designations":
      return "/app/designations";
    case "map":
      return "/app/map";
    case "tool_history":
      return "/app/tool-history";
    default:
      return `/app/${pageKind}`;
  }
}

const PAGE_KIND_LABELS: Record<string, string> = {
  ai_page: "AI page",
  dashboard: "Dashboard",
  bills: "Bills index",
  members: "Members index",
  bill_detail: "Bill page",
  member_detail: "Member page",
  whip_count_bill: "Whip count",
  whip_count_issue: "Whip count (issue)",
  issue_detail: "Issue page",
  committee_detail: "Committee page",
  presentation: "Presentation",
  scorecards: "Scorecards",
  reports: "Reports",
  treasury: "Treasury",
  designations: "Designations",
  map: "Map",
  tool_history: "Tool history",
  intake: "Policy intelligence",
};

/** Rows shown before "Show all". */
export const SINCE_LAST_SEEN_ROW_LIMIT = 5;
const ROW_LIMIT = SINCE_LAST_SEEN_ROW_LIMIT;

export function SinceLastSeen({ workspaceId }: SinceLastSeenProps) {
  // The "since" timestamp we send to the API. Captured ONCE at mount
  // (or at workspaceId change) so the panel doesn't refetch every
  // render. null = first-load on this device → don't render.
  const [sinceIso, setSinceIso] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    setDismissed(false);
    const stored = readLastSeen(workspaceId);
    if (!stored) {
      // First load on this device — stamp now, skip the panel.
      writeLastSeen(workspaceId, new Date().toISOString());
      setSinceIso(null);
    } else {
      setSinceIso(stored);
    }
  }, [workspaceId]);

  const { data: rollup, isLoading } = useQuery<SinceRollup | null>({
    queryKey: ["since-last-seen", workspaceId, sinceIso],
    enabled: !!workspaceId && !!sinceIso,
    queryFn: async () => {
      const url = `/api/v1/workspaces/${encodeURIComponent(workspaceId!)}/since?since=${encodeURIComponent(sinceIso!)}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: 60_000, // don't re-fetch within a minute
  });

  // Compute total signal so we can hide the panel entirely when there's
  // nothing to show (rather than rendering an empty card).
  const totalSignals = useMemo(() => {
    if (!rollup) return 0;
    return (
      rollup.events.length +
      rollup.bills_moved.length +
      rollup.new_attention_items.length +
      rollup.new_briefings.length +
      (rollup.new_policy_intelligence?.length ?? 0) +
      (rollup.new_panel_compositions?.length ?? 0) +
      (rollup.recent_ticket_updates?.length ?? 0)
    );
  }, [rollup]);

  if (!workspaceId || !sinceIso || dismissed) return null;
  if (isLoading || !rollup || totalSignals === 0) return null;

  const handleDismiss = () => {
    if (workspaceId && rollup) writeLastSeen(workspaceId, rollup.now);
    setDismissed(true);
  };

  const wsParam = `?workspace_id=${encodeURIComponent(workspaceId)}`;
  const row = "flex items-start gap-3 px-4 py-2 hover:bg-stone-50";
  const stamp = "text-[11px] text-stone-400 font-mono shrink-0 pt-0.5";

  // Every item becomes one row in its section; the card shows the first
  // ROW_LIMIT rows and a "Show all" toggle for the rest.
  const sections: Array<{ key: string; title: string; rows: React.ReactNode[] }> = [];

  const panels = rollup.new_panel_compositions ?? [];
  if (panels.length > 0) sections.push({
    key: "panels",
    title: `${panels.length} AI composition${panels.length === 1 ? "" : "s"} ready`,
    rows: panels.map((p) => {
      const href = `${panelHref(p.page_kind, p.instance_id)}${wsParam}`;
      const label = p.title ?? PAGE_KIND_LABELS[p.page_kind] ?? p.page_kind;
      return (
        <a key={p.id} href={href} className={`${row} group`}>
          <Sparkles size={15} className="shrink-0 mt-0.5 text-stone-500" aria-hidden />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-stone-900 truncate group-hover:underline">
              {label}
              {p.title && (
                <span className="font-normal text-stone-500 ml-1.5">
                  · {PAGE_KIND_LABELS[p.page_kind] ?? p.page_kind}
                  {p.instance_id !== "default" ? ` (${p.instance_id})` : ""}
                </span>
              )}
            </div>
            {p.summary && <div className="text-[11px] text-stone-600 line-clamp-2 mt-0.5">{p.summary}</div>}
          </div>
          <span className={stamp}>{relativeTime(p.composed_at)}</span>
        </a>
      );
    }),
  });

  if (rollup.bills_moved.length > 0) sections.push({
    key: "bills",
    title: `${rollup.bills_moved.length} tracked bill${rollup.bills_moved.length === 1 ? "" : "s"} moved`,
    rows: rollup.bills_moved.map((b) => (
      <div key={b.bill_ref} className={row}>
        <a href={`/app/bills/${encodeURIComponent(b.bill_ref)}`} className="font-mono text-xs text-action hover:underline shrink-0 pt-0.5">
          {b.bill_ref.toUpperCase()}
        </a>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-stone-800 truncate">{b.short_title || b.title || ""}</div>
          <div className="text-[11px] text-stone-500 truncate">{(b.latest_action_text ?? "").slice(0, 140)}</div>
        </div>
        {b.latest_action_date && <span className={stamp}>{b.latest_action_date}</span>}
      </div>
    )),
  });

  if (rollup.new_attention_items.length > 0) sections.push({
    key: "attention",
    title: `${rollup.new_attention_items.length} thing${rollup.new_attention_items.length === 1 ? "" : "s"} need${rollup.new_attention_items.length === 1 ? "s" : ""} your attention`,
    rows: rollup.new_attention_items.map((a) => {
      const summary = (a.payload?.summary as string | undefined) ?? ATTENTION_LABELS[a.type] ?? a.type;
      return (
        <div key={a.id} className={row}>
          <AlertTriangle size={15} className="shrink-0 mt-0.5 text-amber-700" aria-hidden />
          <div className="flex-1 min-w-0"><div className="text-sm text-stone-800 line-clamp-2">{summary}</div></div>
          <span className={stamp}>{relativeTime(a.created_at)}</span>
        </div>
      );
    }),
  });

  const intelligence = rollup.new_policy_intelligence ?? [];
  if (intelligence.length > 0) sections.push({
    key: "intelligence",
    title: `${intelligence.length} high-priority intelligence update${intelligence.length === 1 ? "" : "s"}`,
    rows: intelligence.map((item) => (
      <a key={item.id} href="/app/intake?needs_review=true#policy-intelligence-heading" className={`${row} group`}>
        {item.priority_tier === "urgent"
          ? <Siren size={15} className="shrink-0 mt-0.5 text-red-700" aria-hidden />
          : <Zap size={15} className="shrink-0 mt-0.5 text-amber-700" aria-hidden />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-stone-900 line-clamp-1 group-hover:underline">{item.title}</div>
          <div className="text-[11px] text-stone-600 line-clamp-2 mt-0.5">{item.summary}</div>
          <div className="text-[10px] text-stone-500 mt-0.5 uppercase tracking-wide">
            {item.priority_tier} · {item.priority_score} · {item.source_count} source{item.source_count === 1 ? "" : "s"}
            {item.updated_since_review
              ? item.new_source_count > 0
                ? ` · updated +${item.new_source_count}`
                : " · updated since review"
              : " · needs review"}
          </div>
        </div>
        <span className={stamp}>{relativeTime(item.evidence_updated_at)}</span>
      </a>
    )),
  });

  if (rollup.new_briefings.length > 0) sections.push({
    key: "briefings",
    title: `${rollup.new_briefings.length} new briefing${rollup.new_briefings.length === 1 ? "" : "s"}`,
    rows: rollup.new_briefings.map((b) => (
      <div key={b.id} className={row}>
        <Newspaper size={15} className="shrink-0 mt-0.5 text-stone-500" aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-stone-800 truncate">{b.title ?? `Briefing for ${b.briefing_date}`}</div>
          {typeof b.item_count === "number" && b.item_count > 0 && (
            <div className="text-[11px] text-stone-500">{b.item_count} item{b.item_count === 1 ? "" : "s"}</div>
          )}
        </div>
        {b.briefing_date && <span className={stamp}>{b.briefing_date}</span>}
      </div>
    )),
  });

  if (rollup.events.length > 0) sections.push({
    key: "events",
    title: `${rollup.events.length} action${rollup.events.length === 1 ? "" : "s"} logged`,
    rows: rollup.events.map((e) => (
      <div key={e.id} className={row}>
        <span className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded bg-stone-100 text-[10px] font-semibold text-stone-700" aria-hidden>
          {e.actor_type === "ai" ? <Bot size={13} /> : e.actor_type === "user" ? <User size={13} /> : <Clock size={13} />}
        </span>
        <div className="flex-1 min-w-0"><div className="text-sm text-stone-800 line-clamp-2">{e.summary}</div></div>
        <span className={stamp}>{relativeTime(e.created_at)}</span>
      </div>
    )),
  });

  // Feedback tickets are not workspace activity: one summary row that
  // points at the Feedback page, never a list of FB-… entries.
  const tickets = rollup.recent_ticket_updates ?? [];
  if (tickets.length > 0) {
    const resolved = tickets.filter((t) => t.status === "resolved").length;
    sections.push({
      key: "tickets",
      title: "Your feedback",
      rows: [(
        <a key="tickets" href={`/app/feedback${wsParam}`} className={`${row} group`}>
          <MessageSquare size={15} className="shrink-0 mt-0.5 text-stone-500" aria-hidden />
          <div className="flex-1 min-w-0 text-sm text-stone-800 group-hover:underline">
            {tickets.length} of your feedback ticket{tickets.length === 1 ? " was" : "s were"} updated
            {resolved > 0 ? ` (${resolved} resolved)` : ""}
          </div>
        </a>
      )],
    });
  }

  const totalRows = sections.reduce((sum, section) => sum + section.rows.length, 0);
  let budget = showAll ? Infinity : ROW_LIMIT;
  const visibleSections = sections.map((section) => {
    const rows = section.rows.slice(0, Math.max(0, budget));
    budget -= rows.length;
    return { ...section, rows };
  }).filter((section) => section.rows.length > 0);
  const hidden = totalRows - visibleSections.reduce((sum, section) => sum + section.rows.length, 0);

  // When the AI composed at least one panel while the user was away,
  // lead with that framing — it's the most actionable surface in this
  // card and the principal should know the AI did work for them.
  const aiPanelCount = panels.length;
  const headerTitle =
    aiPanelCount > 0
      ? `The AI built ${aiPanelCount} panel${aiPanelCount === 1 ? "" : "s"} while you were away`
      : `While you were away (${elapsedHuman(rollup.since)})`;
  const headerSubtitle =
    aiPanelCount > 0
      ? `${elapsedHuman(rollup.since)} since you last visited · ${totalRows} update${totalRows === 1 ? "" : "s"}`
      : `${totalRows} update${totalRows === 1 ? "" : "s"} in this workspace`;

  return (
    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-stone-200">
        <span className="h-2 w-2 shrink-0 rounded-full bg-accent ring-2 ring-ink/10" aria-hidden />
        <div className="flex-1">
          <div className="text-sm font-semibold text-stone-900">{headerTitle}</div>
          <div className="text-[11px] text-stone-500">{headerSubtitle}</div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-stone-400 hover:text-stone-700 text-sm w-6 h-6 rounded hover:bg-stone-100"
          title="Mark these updates seen and dismiss"
          aria-label="Mark these updates seen and dismiss"
        >
          ×
        </button>
      </div>

      <div className="divide-y divide-stone-100">
        {visibleSections.map((section) => (
          <Section key={section.key} title={section.title}>{section.rows}</Section>
        ))}
      </div>
      {(hidden > 0 || (showAll && totalRows > ROW_LIMIT)) && (
        <button
          type="button"
          onClick={() => setShowAll((value) => !value)}
          aria-expanded={showAll}
          className="w-full border-t border-stone-100 px-4 py-2 text-left text-xs font-semibold text-action hover:bg-stone-50"
        >
          {showAll ? "Show fewer" : `Show all ${totalRows} updates`}
        </button>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-mono text-stone-500 px-4 pt-2.5 pb-1">
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}
